import {
  format,
  isAfter,
  isToday,
  isYesterday,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { ExtendedGenerationRecord } from 'dynamo-daos/build/models/projects/projectRecord';
import { Fragment, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { FromStore } from '@cartwhl/common/types';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import LoadingBlock from '@cartwhl/ui/atoms/LoadingBlock';
import Text from '@cartwhl/ui/base/Text';

import {
  animationGenerationStore,
  navigationStore,
  projectStore,
} from 'stores';

import HistoryAccordionNew from './HistoryAccordionNew';

export default function GenerationHistoryNew({
  searchTerm,
}: {
  searchTerm: string;
}) {
  const {
    currentProject,
    currentProjectGenerationHistory: currentProjectJobHistory,
  } = useSnapshot(projectStore.state);
  useEffect(() => {
    if (
      currentProjectJobHistory.status === 'initial' &&
      currentProject.data?.projectID
    ) {
      projectStore.loadCurrentProject(currentProject.data?.projectID);
    }
  }, [currentProjectJobHistory.status, currentProject.data?.projectID]);

  let jobs =
    currentProjectJobHistory.data?.items !== undefined
      ? currentProjectJobHistory.data.items
      : [];

  // filter jobs by search term
  if (searchTerm.length > 1) {
    jobs = jobs.filter((job) => {
      return job.title.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }

  const groupedJobs = groupJobsByCreationDate(jobs);

  return (
    <>
      {currentProjectJobHistory.status === 'initial' ? (
        <div className="grid grid-cols-1 gap-2">
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {Object.entries(groupedJobs).map(([key, jobsGroup]) => (
            <Fragment key={key}>
              <Text
                variant="studio/body/medium/sm"
                as="div"
                className="text-gray-45 flex h-[34px] items-center px-2"
              >
                {key}
              </Text>
              <HistoryAccordionNew jobs={jobsGroup} defaultValue={[]} />
            </Fragment>
          ))}

          {(currentProjectJobHistory.status === 'ready' ||
            currentProjectJobHistory.status === 'pending') && (
            <div className="mt-10 flex justify-center">
              <InfiniteLoadingTrigger
                loading={currentProjectJobHistory.status === 'pending'}
                done={
                  currentProjectJobHistory.status === 'ready' &&
                  currentProjectJobHistory.data?.nextToken === undefined
                }
                onEnter={projectStore.reloadCurrentProjectGenerationHistory}
              />
            </div>
          )}

          {currentProjectJobHistory.status === 'ready' && jobs.length === 0 && (
            <Text
              variant="studio/body/sm"
              className="text-gray-60 w-full text-center"
            >
              {searchTerm.length > 1
                ? 'No results found for your search. Try a different search term.'
                : 'This project has no history yet. Are you looking for a motion from a previous project?'}
            </Text>
          )}
        </div>
      )}
    </>
  );
}

const DateGroups = {
  ['Today']: isToday,
  ['Yesterday']: isYesterday,
  ['Previous 7 days']: (date: string) => isAfter(date, subDays(new Date(), 7)),
  ['Previous 30 days']: (date: string) =>
    isAfter(date, subMonths(new Date(), 1)),
  ['rest']: () => true,
};

function groupJobsByCreationDate(jobs?: FromStore<ExtendedGenerationRecord[]>) {
  const output: Record<string, FromStore<ExtendedGenerationRecord>[]> = {};

  for (const job of jobs || []) {
    for (const [key, test] of Object.entries(DateGroups)) {
      if (key === 'rest') {
        if (isAfter(job.createdAt, subYears(new Date(), 1))) {
          output[format(job.createdAt, 'MMMM')] =
            output[format(job.createdAt, 'MMMM')] || [];
          output[format(job.createdAt, 'MMMM')].push(job);
        } else {
          output[format(job.createdAt, 'MMMM yyy')] =
            output[format(job.createdAt, 'MMMM yyy')] || [];
          output[format(job.createdAt, 'MMMM yyy')].push(job);
        }
      } else if (test(job.createdAt)) {
        output[key] = output[key] || [];
        output[key].push(job);
        break;
      }
    }
  }

  return output;
}
